import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { PersonRef } from '@/features/catalog/api'
import { isApiError, type ListMeta } from '@/lib/api'
import { useApi } from '@/lib/api-context'
import { useIdempotencyKey } from '@/lib/use-idempotency-key'

/**
 * Anamneses do lado da clínica (docs/api/anamneses.md; regras em docs/areas/anamnese.md, RN-ANA-01
 * a 05 e 20 a 22). A clínica emite, acompanha, reenvia, cancela e lê; quem responde é a paciente,
 * pelo link (`features/anamnesis-form`).
 */

export type AnamnesisStatus = 'issued' | 'in_progress' | 'completed' | 'expired' | 'canceled'
export type AnamnesisChannel = 'whatsapp' | 'email' | 'both' | 'link'
export type AnamnesisFilter = 'all' | 'completed' | 'not_completed'
export type AnamnesisSort = 'sentAt' | '-sentAt' | 'patient' | 'status'
export type DeliveryEvent = 'sent' | 'delivered' | 'read' | 'failed'

export interface AnamnesisPatient {
  id: string
  name: string
  firstName: string
  ageAtSendTime: number | null
}

export interface AnamnesisLink {
  /** Só em quem pode reenviar: na emissão, no reenvio e no registro da não preenchida. */
  url?: string
  issuedAt: string
  expiresAt: string
  resendCount: number
}

export interface AnamnesisDelivery {
  channel: 'whatsapp' | 'email'
  event: DeliveryEvent
  at: string
  detail?: string | null
}

/** O rascunho como a clínica o vê: onde a paciente parou e quando salvou. Nunca as respostas. */
export interface AnamnesisDraft {
  cursor: { sectionKey: string; questionId: string }
  startedAt?: string
  savedAt: string
}

export interface AnamnesisCancellation {
  at: string
  /** `null` quando quem cancelou não existe mais. */
  by: PersonRef | null
  reason: string | null
}

/** O resumo das listas (`GET /anamneses`). */
export interface AnamnesisSummary {
  id: string
  status: AnamnesisStatus
  /** A etiqueta por extenso, pronta: "Preenchida", "Não preenchida · em preenchimento"… */
  label: string
  patient: AnamnesisPatient
  procedureAtSendTime: { procedureId: string; name: string } | null
  practitioner: { practitionerId: string; name: string } | null
  /** Nulo na ficha importada do sistema anterior. */
  channel: AnamnesisChannel | null
  link: AnamnesisLink
  delivery: AnamnesisDelivery[]
  draft?: AnamnesisDraft | null
  /** Quando o rascunho foi apagado, 90 dias depois de o link vencer (P-ANA-06). */
  draftPurgedAt?: string | null
  /** Quem chama pode abrir a leitura completa (prestadora responsável, gestora, root). */
  canRead: boolean
  createdAt: string
  /** `null` na ficha importada do sistema antigo: ninguém do novo a emitiu. */
  createdBy: PersonRef | null
  cancellation?: AnamnesisCancellation | null
  /** Só em item de clínica irmã, na rede que compartilha pacientes (RT-06): leitura, sem escrita. */
  clinic?: { id: string; name: string } | null
}

export interface AnamnesisReadingQuestion {
  questionId?: string
  key?: string
  shortLabel: string
  label?: string
  answerText: string
  isFlagged?: boolean
  /** Ficha importada: as outras respostas que o sistema anterior guardou para a mesma pergunta. */
  variants?: { answerText: string }[]
}

export interface AnamnesisReadingSection {
  key: string
  name: string
  questions: AnamnesisReadingQuestion[]
}

export interface AnamnesisAnswer {
  questionId: string
  key: string
  value?: string | number | string[]
  followUp?: string
  isSkipped: boolean
  isNotApplicable: boolean
  variants?: { value?: string | number | string[]; followUp?: string }[]
}

export interface AnamnesisConfirmedDetails {
  name: string
  birthDate: string
  docType?: 'cpf'
  docNumber?: string
  foreignDocument?: { type: string; number: string; country: string }
  occupation?: string | null
  email: string
  whatsapp: string
  address: {
    postalCode: string
    street: string
    number: string
    complement?: string | null
    district: string
    city: string
    state: string
    country?: string
  }
  maritalStatus: string
  guardian?: { name: string; docType: 'cpf'; docNumber: string; whatsapp: string }
}

export type CopyEvent = 'pending' | 'sent' | 'delivered' | 'bounced'

/** A prova do envio (RT-15). */
export interface AnamnesisEvidence {
  protocolNumber: string
  hash: string
  shortHash: string
  submittedAt: string
  ip: string
  userAgent: string
  device: string
  declarationAcceptance: { version: string; text: string; acceptedAt: string }
  copy: { email: string; sentAt: string | null; event: CopyEvent }
}

export type RecordIssue = 'doc_number_in_other_record' | 'email_in_other_record'

/**
 * `GET /anamneses/:id`. Não preenchida: o resumo com `link.url` e o rascunho. Respondida: a leitura
 * completa (`attention`, `mainSections`, `collapsedSections`, `evidence`), para quem pode.
 */
export interface Anamnesis extends AnamnesisSummary {
  questionnaireVersionId?: string
  clinicQuestionVersionId?: string | null
  questionSet?: { questionIds: string[] }
  /** "Anamnese de 28/04/2026 · 09:51 · hash a3f9c1e7…4b82d5", pronto. */
  evidenceHeader?: string
  attention?: { questionKey: string; label: string }[]
  mainSections?: AnamnesisReadingSection[]
  collapsedSections?: AnamnesisReadingSection[]
  confirmedDetails?: AnamnesisConfirmedDetails | null
  answers?: AnamnesisAnswer[]
  evidence?: AnamnesisEvidence | null
  recordChanges?: { field: string; from: string | null; to: string | null }[]
  recordIssues?: RecordIssue[]
  recordIssuesResolvedAt?: string | null
  recordIssuesResolvedBy?: PersonRef | null
  referralSource?: { value: string; followUp?: string | null; answeredAt: string } | null
  appointments?: { appointmentId: string; createdAt: string }[]
  isImported?: boolean
  completedAt?: string | null
}

/** O que `POST /anamneses` devolve: a anamnese emitida, com o link e a frase do toast. */
export interface IssuedAnamnesis extends Anamnesis {
  message: string
}

/** O que `POST /anamneses/:id/resend` devolve. */
export interface ResentAnamnesis {
  id: string
  status: AnamnesisStatus
  link: AnamnesisLink
  delivery: AnamnesisDelivery[]
  message: string
}

export interface CanceledAnamnesis {
  id: string
  status: 'canceled'
  label: string
  cancellation: AnamnesisCancellation
}

export interface IssueAnamnesisInput {
  patientId: string
  channel: AnamnesisChannel
  procedureId?: string
  practitionerId?: string
}

export interface AnamnesisListMeta extends ListMeta {
  counts: Record<AnamnesisFilter, number>
}

export const anamnesisKeys = {
  all: ['anamneses'] as const,
  list: (params: object) => ['anamneses', 'list', params] as const,
  detail: (id: string) => ['anamneses', 'detail', id] as const,
}

export function useAnamneses(params: {
  filter: AnamnesisFilter
  patientId?: string
  search?: string
  sort?: AnamnesisSort
  page?: number
  perPage?: number
}) {
  const api = useApi()
  return useQuery({
    queryKey: anamnesisKeys.list(params),
    queryFn: () =>
      api.list<AnamnesisSummary, AnamnesisListMeta>('/anamneses', {
        filter: params.filter,
        patientId: params.patientId,
        search: params.search,
        sort: params.sort,
        page: params.page ?? 1,
        perPage: params.perPage ?? 25,
      }),
    // Trocar de filtro ou de página mantém a lista à vista; trocar de paciente, não: as anamneses
    // de uma pessoa nunca aparecem na ficha de outra.
    placeholderData: (previous, previousQuery) => {
      const before = previousQuery?.queryKey[2] as { patientId?: string } | undefined
      return before?.patientId === params.patientId ? previous : undefined
    },
  })
}

export function useAnamnesis(id: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: anamnesisKeys.detail(id ?? ''),
    queryFn: () => api.get<Anamnesis>(`/anamneses/${id}`),
    enabled: Boolean(id),
    // 403 e 404 são respostas, não falhas de rede: repetir não muda nada.
    retry: false,
  })
}

/** Emite a anamnese e envia o link (RN-ANA-01). Repetir o mesmo corpo não emite duas vezes. */
export function useIssueAnamnesis() {
  const api = useApi()
  const queryClient = useQueryClient()
  const idempotency = useIdempotencyKey()
  const mutation = useMutation({
    mutationFn: (input: IssueAnamnesisInput) =>
      api.post<IssuedAnamnesis>('/anamneses', input, { idempotencyKey: idempotency.keyFor(input) }),
    onSuccess: () => {
      idempotency.reset()
      return queryClient.invalidateQueries({ queryKey: anamnesisKeys.all })
    },
    // Com o provedor fora (503) a anamnese foi criada mesmo assim: a lista precisa mostrá-la.
    onError: () => queryClient.invalidateQueries({ queryKey: anamnesisKeys.all }),
  })
  /**
   * Encerra a intenção sem ter criado: depois de cancelar a anamnese que barrava a emissão, o mesmo
   * corpo é outra intenção e pede chave nova.
   */
  return { ...mutation, renewKey: idempotency.reset }
}

/** Reemite o link da mesma anamnese (RN-ANA-04). Sem `channel`, vale o canal da emissão. */
export function useResendAnamnesis() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, channel }: { id: string; channel?: AnamnesisChannel }) =>
      api.post<ResentAnamnesis>(`/anamneses/${id}/resend`, channel ? { channel } : {}),
    onSettled: () => queryClient.invalidateQueries({ queryKey: anamnesisKeys.all }),
  })
}

/** Cancela uma não preenchida (RN-ANA-05): o link deixa de valer e o rascunho é apagado. */
export function useCancelAnamnesis() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      api.post<CanceledAnamnesis>(`/anamneses/${id}/cancel`, reason ? { reason } : {}),
    onSettled: () => queryClient.invalidateQueries({ queryKey: anamnesisKeys.all }),
  })
}

/** O que a emissão precisa saber da paciente: o nome e os contatos que cada canal exige. */
export interface PatientContact {
  id: string
  name: string
  email: string | null
  whatsapp: string | null
}

/**
 * A paciente da ficha (docs/api/pacientes.md, `GET /patients/:id`), só com o que a emissão usa. A
 * chave é a mesma do detalhe em `features/patients`: com a ficha aberta, o cadastro já está no
 * cache e nada é pedido de novo.
 */
export function usePatientContact(patientId: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: ['patients', 'detail', patientId ?? ''],
    queryFn: () => api.get<PatientContact>(`/patients/${patientId}`),
    enabled: Boolean(patientId),
    retry: false,
  })
}

/** Busca de paciente para "+ Enviar anamnese" na lista global (`GET /patients?search=`). */
export function usePatientSearch(search: string) {
  const api = useApi()
  return useQuery({
    queryKey: ['anamneses', 'patient-search', search],
    queryFn: () =>
      api.list<PatientContact>('/patients', { search, filter: 'all', page: 1, perPage: 10 }),
    enabled: search.length >= 2,
    placeholderData: (previous) => previous,
  })
}

export interface IssueOption {
  id: string
  name: string
}

/** Lê todas as páginas de uma lista de escolha: a API pagina de 50 em 50. */
function useAllPages(path: string, filter: string, key: string) {
  const api = useApi()
  return useQuery({
    queryKey: ['anamneses', key],
    queryFn: async () => {
      const options: IssueOption[] = []
      for (let page = 1; ; page++) {
        const result = await api.list<IssueOption>(path, {
          filter,
          sort: 'name',
          page,
          perPage: 50,
        })
        options.push(...result.data)
        if (page >= result.meta.totalPages) return options
      }
    },
  })
}

/** Os procedimentos não arquivados do catálogo da clínica (docs/api/admin.md), para a emissão. */
export function useProcedureOptions() {
  return useAllPages('/admin/procedures', 'all', 'procedure-options')
}

/** As prestadoras ativas da clínica, para a emissão. */
export function usePractitionerOptions() {
  return useAllPages('/admin/practitioners', 'active', 'practitioner-options')
}

/**
 * O provedor do canal está fora (503 `unavailable`): na emissão a anamnese foi criada mesmo assim,
 * e o corpo do erro traz `anamnesisId` e `link.url` para a tela oferecer o link ou outro canal.
 */
export function providerDown(
  error: unknown,
): { anamnesisId: string | null; url: string | null } | null {
  if (!isApiError(error) || error.code !== 'unavailable') return null
  const link = error.details.link as { url?: unknown } | undefined
  const anamnesisId = error.details.anamnesisId
  return {
    anamnesisId: typeof anamnesisId === 'string' ? anamnesisId : null,
    url: typeof link?.url === 'string' ? link.url : null,
  }
}

/** A paciente já tem uma não preenchida (409, RN-ANA-02): o id dela vem no corpo do erro. */
export function pendingAnamnesisId(error: unknown): string | null {
  if (!isApiError(error) || error.code !== 'conflict') return null
  const anamnesisId = error.details.anamnesisId
  return typeof anamnesisId === 'string' ? anamnesisId : null
}

// Questionário --------------------------------------------------------------------------------------
//
// Os objetos de `apps/api/supabase/functions/anamneses/questionnaire.ts` (docs/areas/anamnese.md,
// "Entidades e campos"): o padrão da Westhetics, versionado (RN-ANA-18), e as perguntas da clínica,
// publicadas a cada salvamento (RN-ANA-19, P-ANA-08).

export type SectionKey = 'aboutYou' | 'yourHealth' | 'yourExpectations' | 'selfImage'
export const SECTION_KEYS: readonly SectionKey[] = [
  'aboutYou',
  'yourHealth',
  'yourExpectations',
  'selfImage',
]

export type QuestionType =
  'text' | 'yes_no' | 'single_choice' | 'multiple_choice' | 'scale' | 'date' | 'number'
export const QUESTION_TYPES: readonly QuestionType[] = [
  'text',
  'yes_no',
  'single_choice',
  'multiple_choice',
  'scale',
  'date',
  'number',
]
/** RN-ANA-19: a pergunta da clínica é texto, sim ou não, ou escolha. */
export type ClinicQuestionType = 'text' | 'yes_no' | 'single_choice' | 'multiple_choice'
export const CLINIC_QUESTION_TYPES: readonly ClinicQuestionType[] = [
  'text',
  'yes_no',
  'single_choice',
  'multiple_choice',
]

export type FollowUpWhen = 'yes' | 'no' | 'always' | 'options'
export type FlagRuleWhen = 'yes' | 'no' | 'options' | 'below' | 'above'

export interface QuestionOption {
  id: string
  /** Chave estável: é o que a resposta guarda. */
  value: string
  label: string
  examples: string | null
  position: number
  opensFollowUp: boolean
  isExclusive: boolean
  raisesFlag: boolean
  attentionLabel: string | null
}

export interface QuestionCondition {
  questionKey: string
  values: string[]
}

export interface QuestionFollowUp {
  when: FollowUpWhen
  label: string
  example: string
  isRequired: boolean
  maxLength: number
}

export interface QuestionFlagRule {
  when: FlagRuleWhen
  /** O limite de `below` e `above`, em pergunta de número. */
  value?: number | null
  attentionLabel: string
}

export interface Question {
  id: string
  source: 'default' | 'clinic' | 'legacy'
  key: string
  position: number
  label: string
  shortLabel: string
  context: string | null
  type: QuestionType
  allowsUnsure: boolean
  isRequired: boolean
  isSkippable: boolean
  isSensitive: boolean
  condition: QuestionCondition | null
  followUp: QuestionFollowUp | null
  flagRule: QuestionFlagRule | null
  example: string | null
  /** Faixa de `number`. Em `text`, `max` é o tamanho máximo (padrão 2.000). */
  min: number | null
  max: number | null
  review: { legal: boolean; clinical: boolean }
  options: QuestionOption[]
}

export interface QuestionnaireSection {
  id: string
  key: SectionKey | string
  name: string
  position: number
  intro: string | null
  isSensitive: boolean
  canHaveClinicQuestions: boolean
  questions: Question[]
}

export interface Declaration {
  version: string | null
  text: string | null
}

/** `GET /anamneses/questionnaire`: o padrão vigente mais as perguntas da clínica, na ordem final. */
export interface Questionnaire {
  questionnaireVersionId: string
  questionnaireVersionNumber: number | null
  clinicQuestionVersionId: string | null
  clinicQuestionVersionNumber: number | null
  declaration: Declaration
  sections: QuestionnaireSection[]
  questionCount: number
  clinicQuestionCount: number
}

/** Uma versão do questionário padrão (`GET /anamneses/questionnaire/versions[/:id]`). */
export interface QuestionnaireVersion {
  id: string
  number: number | null
  status: 'draft' | 'published'
  declaration: Declaration
  source: string | null
  publishedAt: string | null
  publishedBy: PersonRef | null
  publishJustification: string | null
  createdAt: string
  createdBy: PersonRef | null
  updatedAt: string
  questionCount: number
  isCurrent: boolean
  /** Só na leitura da versão inteira. */
  sections?: QuestionnaireSection[]
}

/** O que `PUT /anamneses/questionnaire/versions/:id` devolve: a versão mais o que impede publicar. */
export interface SavedQuestionnaireVersion extends QuestionnaireVersion {
  sections: QuestionnaireSection[]
  warnings: string[]
}

/** O que `PUT /anamneses/questionnaire/clinic-questions` devolve. */
export interface ClinicQuestionVersion {
  id: string
  number: number
  publishedAt: string | null
  publishedBy: PersonRef | null
  questions: Question[]
}

// O que a borda aceita (os esquemas Zod `optionInput`, `questionInput`, `clinicQuestionInput`,
// `sectionInput`, `clinicQuestionsBody`, `updateVersionBody`, `createVersionBody` e
// `publishVersionBody`). Campos nulos ou ausentes são o mesmo que vazios.

export interface QuestionOptionInput {
  value: string
  label: string
  examples?: string | null
  opensFollowUp?: boolean
  isExclusive?: boolean
  raisesFlag?: boolean
  attentionLabel?: string | null
}

export interface QuestionInput {
  key: string
  label: string
  shortLabel: string
  context?: string | null
  type: QuestionType
  allowsUnsure?: boolean
  isRequired?: boolean
  isSkippable?: boolean
  isSensitive?: boolean
  options?: QuestionOptionInput[]
  condition?: QuestionCondition | null
  followUp?: QuestionFollowUp | null
  flagRule?: QuestionFlagRule | null
  example?: string | null
  min?: number | null
  max?: number | null
  review?: { legal: boolean; clinical: boolean }
}

export interface ClinicQuestionsInput {
  /** A versão que a gestora carregou, ou `null` se não havia; divergente → 409. */
  currentVersionId: string | null
  questions: QuestionInput[]
}

export interface SectionInput {
  key: SectionKey
  name: string
  intro?: string | null
  isSensitive?: boolean
  questions: QuestionInput[]
}

export interface UpdateVersionInput {
  declaration: { version: string; text: string }
  source?: string | null
  sections: SectionInput[]
}

export interface CreateVersionInput {
  basedOnVersionId?: string | null
}

export interface PublishVersionInput {
  /** 10 a 500 caracteres: publicar é ato de trilha com justificativa (RT-22). */
  justification: string
}

export const questionnaireKeys = {
  all: ['anamneses', 'questionnaire'] as const,
  current: (params: object) => ['anamneses', 'questionnaire', 'current', params] as const,
  versions: (params: object) => ['anamneses', 'questionnaire', 'versions', params] as const,
  version: (id: string) => ['anamneses', 'questionnaire', 'version', id] as const,
}

/** O questionário como uma anamnese emitida agora o teria (RN-ANA-18, RN-ANA-19). */
export function useQuestionnaire(
  params: { versionId?: string; clinicQuestionVersionId?: string } = {},
) {
  const api = useApi()
  return useQuery({
    queryKey: questionnaireKeys.current(params),
    queryFn: () => api.get<Questionnaire>('/anamneses/questionnaire', params),
    // 404 ("ainda não foi publicado") é resposta, não falha de rede.
    retry: false,
  })
}

/** Publica uma versão nova das perguntas da clínica (RN-ANA-19). A lista inteira vai a cada vez. */
export function usePublishClinicQuestions() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: ClinicQuestionsInput) =>
      api.put<ClinicQuestionVersion>('/anamneses/questionnaire/clinic-questions', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: questionnaireKeys.all }),
  })
}

/**
 * A ficha de anamnese em branco, em PDF, para imprimir e a paciente preencher à mão (RN-ANA-24,
 * P-ANA-10). Com `patientId`, nome e CPF da paciente já saem impressos.
 */
export function useBlankQuestionnairePdf() {
  const api = useApi()
  return useMutation({
    mutationFn: (patientId?: string) =>
      api.download('/anamneses/questionnaire/pdf', patientId ? { patientId } : undefined),
  })
}

/** As versões do padrão, da mais nova para a mais antiga (root). */
export function useQuestionnaireVersions(params: { page?: number; perPage?: number } = {}) {
  const api = useApi()
  return useQuery({
    queryKey: questionnaireKeys.versions(params),
    queryFn: () =>
      api.list<QuestionnaireVersion>('/anamneses/questionnaire/versions', {
        page: params.page ?? 1,
        perPage: params.perPage ?? 25,
      }),
    placeholderData: (previous) => previous,
  })
}

/** Uma versão inteira, rascunho ou publicada, com os quatro blocos (root). */
export function useQuestionnaireVersion(id: string | undefined) {
  const api = useApi()
  return useQuery({
    queryKey: questionnaireKeys.version(id ?? ''),
    queryFn: () => api.get<QuestionnaireVersion>(`/anamneses/questionnaire/versions/${id}`),
    enabled: Boolean(id),
    retry: false,
  })
}

/**
 * Cria o rascunho de versão a partir da vigente ou de uma indicada (RN-ANA-18). Só existe um
 * rascunho por vez: repetir responde 409. A chave de idempotência vai pela regra de todo `POST`
 * que cria; a rota hoje não a lê (o 409 já protege), e passa a valer quando ler.
 */
export function useCreateQuestionnaireVersion() {
  const api = useApi()
  const queryClient = useQueryClient()
  const idempotency = useIdempotencyKey()
  return useMutation({
    mutationFn: (input: CreateVersionInput = {}) =>
      api.post<QuestionnaireVersion>('/anamneses/questionnaire/versions', input, {
        idempotencyKey: idempotency.keyFor(input),
      }),
    onSuccess: () => {
      idempotency.reset()
      return queryClient.invalidateQueries({ queryKey: questionnaireKeys.all })
    },
    // No 409 o rascunho já existe: a lista precisa mostrá-lo.
    onError: () => queryClient.invalidateQueries({ queryKey: questionnaireKeys.all }),
  })
}

/** Substitui o conteúdo de um rascunho; os `warnings` dizem o que ainda impede publicar. */
export function useUpdateQuestionnaireVersion(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateVersionInput) =>
      api.put<SavedQuestionnaireVersion>(`/anamneses/questionnaire/versions/${id}`, input),
    onSuccess: (saved) => {
      queryClient.setQueryData(questionnaireKeys.version(id), saved)
      return queryClient.invalidateQueries({ queryKey: questionnaireKeys.all })
    },
  })
}

/** Publica o rascunho: imutável, número sequencial, vigente para emissões novas (RN-ANA-18). */
export function usePublishQuestionnaireVersion(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: PublishVersionInput) =>
      api.post<QuestionnaireVersion>(`/anamneses/questionnaire/versions/${id}/publish`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: questionnaireKeys.all }),
  })
}
